import { useMemo } from "react";

import { useIsSuperAdmin } from "@/api/is-superadmin";
import { getUserInitials } from "@/app/utils/get-user-initials";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import {
  Guest,
  GuestType,
  PublicEvent,
  RoleEnum,
} from "@classview/shared-types";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { AttendeesActions } from "./attendees-actions";

type ViewParticipantsTableProps = {
  guests: Guest[];
  event: PublicEvent;
};

export function ViewParticipantsTable({
  guests,
  event,
}: ViewParticipantsTableProps) {
  const { hasPermission, organisation } = useOrganisation();
  const { isSuperAdmin } = useIsSuperAdmin();
  const columnHelper = createColumnHelper<Guest>();

  const hasOrgBasedEditPerm =
    hasPermission(RoleEnum.EVENTS_CREATE) &&
    event.session.organisation?.id === organisation.id;
  const isAllowedToEditEvent = hasOrgBasedEditPerm || isSuperAdmin;

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "name",
        header: "Room Systems",
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-2">
              <Avatar>
                <AvatarImage src={""} />
                <AvatarFallback className="bg-border capitalize text-muted">
                  {getUserInitials(
                    row.original.firstName || "",
                    row.original.lastName || "",
                  )}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span>
                  {row.original.firstName} {row.original.lastName}
                </span>
                <span className="text-muted-foreground">
                  {row.original.email}
                </span>
              </div>
            </div>
          );
        },
      }),
      ...(isAllowedToEditEvent
        ? [
            columnHelper.display({
              id: "actions",
              header: "",
              cell: ({ row }) => {
                if (
                  row.original.type === GuestType.Guest ||
                  row.original.isHost
                ) {
                  return null;
                }

                return (
                  <AttendeesActions
                    event={event}
                    attendeeEmail={row.original.email}
                  />
                );
              },
            }),
          ]
        : []),
    ],
    [columnHelper, event, isAllowedToEditEvent],
  );
  const table = useReactTable({
    data: guests ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <Table>
      <TableBody>
        {table.getRowModel().rows.map((row) => (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
